<template>
  <div id="invoice">
    <div v-if="client" class="client">
      <div class="company">{{ client.company }}</div>
      <div v-if="client.with_regards_to" class="attn">
        {{ client.with_regards_to }}
      </div>
      <div class="address">
        <div class="street">{{ client.street }} {{ client.house_number }}</div>
        <div class="city">{{ client.postal_code }} {{ client.city }}</div>
        <div class="country">{{ client.country }}</div>
      </div>
      <div class="vat">{{ client.VAT }}</div>
    </div>
    <div class="own">
      <div class="address">
        <div class="street">{{ company.street }} {{ company.houseNumber }}</div>
        <div class="city">B-{{ company.postalCode }} {{ company.city }}</div>
      </div>

      <div class="website">{{ company.website }}</div>
      <div class="email">{{ company.email }}</div>

      <div class="vat">VAT# {{ company.vat }}</div>
      <div class="account">Bank# {{ company.account }}</div>
    </div>
    <div class="content">
      <div class="invoice-number">
        <label>INVOICE</label>
        <div class="value">#{{ invoice.number }}</div>
      </div>
      <div class="detail">
        <div class="date">
          <label>DATE</label>
          <div class="value">
            {{ moment(invoice.date).format("D MMM YYYY") }}
          </div>
        </div>
        <div class="spacer">●</div>
        <div class="due-date">
          <label>DUE DATE</label>
          <div class="value">
            {{
              moment(invoice.date)
                .add(invoice.payment_period, "d")
                .format("D MMM YYYY")
            }}
          </div>
        </div>
      </div>
      <div v-if="invoice.title" class="general-title">{{ invoice.title }}</div>
      <div v-if="invoice.articles" class="articles">
        <div class="article-layout legend">
          <div v-if="showDate" class="legend-item date">Date</div>
          <div class="legend-item description">DESCRIPTION</div>
          <div class="legend-item quantity">QTY</div>
          <div class="legend-item price-unit">UNIT PRICE</div>
          <div class="legend-item price">TOTAL</div>
        </div>
        <div
          class="article-layout article"
          v-for="(article, index) of invoice.articles"
          :key="index"
        >
          <div v-if="moment(article.date).isValid()" class="date">
            {{ moment(article.date).format("YYYY / MM / DD") }}
          </div>
          <div class="article-item description">
            <div class="desc-1">{{ article.description }}</div>
            <div class="desc-2">{{ article.sub_description }}</div>
            <div class="desc-3">{{ article.third_description }}</div>
          </div>
          <div class="article-item quantity">{{ article.quantity }}</div>
          <div class="article-item price-unit">{{ article.price_unit }}</div>
          <div class="article-item price">
            {{ calculateArticleTotal(article) }}
          </div>
        </div>
      </div>
      <div class="totals">
        <div v-if="invoice.discount > 0" class="totals-item discount">
          <div class="label">{{ invoice.discount }}% discount</div>
          <div class="value">-{{ discount() }}</div>
        </div>
        <div class="totals-item sub-total">
          <div class="label">Subtotal</div>
          <div class="value">{{ subTotal() }}</div>
        </div>
        <div class="totals-item vat-percentage">
          <div class="label">{{ invoice.vat_percentage }}% VAT</div>
          <div class="value">{{ vat() }}</div>
        </div>
        <div class="totals-item total">
          <div class="label">Total</div>
          <div class="value">{{ total() }}</div>
        </div>
      </div>

      <div class="legal">
        terms and conditions
        <div>{{ company.legal }}</div>
      </div>
      <slot></slot>
    </div>
  </div>
</template>

<script>
import moment from "moment";
export default {
  name: "bonos-template-v1",
  props: ["invoice", "client", "user", "company", "url"],
  data() {
    return {
      moment: moment,
      subTotalWithoutDiscount() {
        let subTotal = 0;
        for (let article of this.invoice.articles) {
          subTotal += article.price_unit * article.quantity;
        }
        return +(Math.round(subTotal + "e+2") + "e-2");
      },
      discount() {
        if (this.invoice.discount) {
          return (this.subTotalWithoutDiscount() / 100) * this.invoice.discount;
        }
        return 0;
      },
      subTotal() {
        let subTotal = 0;
        if (this.invoice.articles) {
          for (let article of this.invoice.articles) {
            subTotal += article.price_unit * article.quantity;
          }
        }
        return +(Math.round(subTotal - this.discount() + "e+2") + "e-2");
      },
      vat() {
        let vat = (this.subTotal() / 100) * this.invoice.vat_percentage;
        return +(Math.round(vat + "e+2") + "e-2");
      },
      total() {
        let total = this.subTotal() + this.vat();
        return +(Math.round(total + "e+2") + "e-2");
      },
    };
  },
  computed: {
    showDate() {
      for (let article of this.invoice.articles) {
        if (moment(article.date).isValid()) {
          return true;
        }
      }
      return false;
    },
  },
  methods: {
    calculateArticleTotal(article) {
      let articleTotal = article.price_unit * article.quantity;
      return +(Math.round(articleTotal + "e+2") + "e-2");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/colors";

@font-face {
  font-family: "geomanist-regular";
  src: url("/assets/fonts/geomanist-regular-webfont.woff2") format("woff2"),
    url("/assets/fonts/geomanist-regular-webfont.woff") format("woff");
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: "geomanist-bold";
  src: url("/assets/fonts/geomanist-bold-webfont.woff2") format("woff2"),
    url("/assets/fonts/geomanist-bold-webfont.woff") format("woff");
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: "kotori-rose-regular";
  src: url("/assets/fonts/kotorirose-regular-webfont.woff2") format("woff2"),
    url("/assets/fonts/kotorirose-regular-webfont.woff") format("woff");
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: "kotori-rose-bold";
  src: url("/assets/fonts/kotorirose-bold-webfont.woff2") format("woff2"),
    url("/assets/fonts/kotorirose-bold-webfont.woff") format("woff");
  font-weight: normal;
  font-style: normal;
}

#invoice {
  color: #f9f3db;
  position: relative;
  font-family: "geomanist-regular";
  letter-spacing: 1px;

  .client {
    position: absolute;
    left: 60px;
    top: 200px;
    font-size: 12px;
    .company {
      font-size: 16px;
      font-family: "kotori-rose-bold";
    }
    .address {
      margin-top: 10px;
    }
  }
  .own {
    color: #ebcea4;
    text-align: right;
    position: absolute;
    right: 76px;
    top: 200px;
    font-size: 12px;
    .address {
      margin: 0 0 10px 0;
    }
    .vat {
      margin-top: 10px;
    }
    .account {
    }
  }

  .content {
    padding-top: 380px;
    display: flex;
    flex-direction: column;
  }

  .invoice-number {
    margin: 0 0 24px 60px;
    font-family: "kotori-rose-regular";
    font-size: 12px;
    
    label {
      opacity: 50%;
      letter-spacing: 4px;
    }
    .value {
      font-size: 18px;
    }
    
  }
  
  .detail {
    font-family: "kotori-rose-regular";
    margin: 0 0 24px 60px;
    display: flex;
    align-items: center;
    .date,
    .due-date {
      display: flex;
      flex-direction: column;
      label {
        letter-spacing: 4px;
        opacity: 50%;
        font-size: 12px;
        font-weight: 700;
      }
      .value {
        font-size: 15px;
      }
    }
    .spacer {
      font-size: 10px;
      margin: 0 12px;
      opacity: 50%;
    }
  }

  .general-title {
    width: 100%;
    text-align: left;
    padding: 5px 0;
    margin: 10px 60px;
    font-size: 14px;
    font-weight: 700;
    color: #56682a;
  }

  .articles {
    padding: 10px 60px;
    width: 21cm;
    box-sizing: border-box;
    .legend {
      margin-bottom: 20px;
      font-family: "kotori-rose-regular";
      letter-spacing: 4px;
    }
  }

  .discount {
    display: flex;
    align-items: center;
    .value {
      padding: 3px;
    }
  }
  .totals {
    right: 40px;
    bottom: 100px;
    position: absolute;
    .totals-item {
      text-align: right;
      display: flex;
      align-items: center;
      .label {
        font-size: 14px;
        width: 90px;
        margin-right: 5px;
      }
      .value {
        font-size: 14px;
        padding: 3px;
        text-align: right;
        width: 100px;
        
        &:after {
          content: "EUR";
          font-size: 10px;
          margin-left: 5px;
        }
      }
    }
    .discount {
      margin-bottom: 20px;
      color: var(--color-invoice-theme);
    }
    .total {
      color: #ebcea4;
      margin-top: 10px;
      .value {
        font-size: 20px;
      }
    }
  }

  .legal {
    position: absolute;
    bottom: 24px;
    font-size: 10px;
    font-weight: 700;
    text-align: right;
    color: #999;
    right: 40px;
  }
}
.invoice-image-template {
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.article {
  margin-top: 5px;
  color: #ebcea4;
}

.article-layout {
  display: flex;
  .article-item {
    font-size: 14px;
    font-weight: 400;
  }
  .legend-item {
    font-size: 12px;
    font-weight: 700;
    opacity: 50%;
  }
  .date {
    flex-basis: 100px;
    font-size: 14px;
    flex-grow: 0;
    flex-shrink: 0;
    padding-right: 12px;
  }
  .description {
    flex-grow: 1;
    padding-right: 8px;
    .desc-2 {
      font-size: 14px;
    }
    .desc-3 {
      font-size: 12px;
      font-weight: 700;
    }
  }
  .unit {
    flex-basis: 50px;
    flex-grow: 0;
    flex-shrink: 0;
    text-align: center;
  }
  .price-unit {
    flex-basis: 130px;
    flex-grow: 0;
    flex-shrink: 0;
    text-align: right;
  }
  .quantity {
    text-align: center;
    flex-basis: 60px;
    flex-grow: 0;
    flex-shrink: 0;
  }
  .price {
    text-align: right;
    flex-basis: 75px;
    flex-grow: 0;
    flex-shrink: 0;
  }
}

@media print {
  #invoice-template {
    border: none;
  }
}
</style>
